import { useController, useFormContext as useReactHookFormContext } from 'react-hook-form';
import type { FieldHookConfig } from 'src/lib/components/form/formTypes';

export const useField = <TValue = any>(
    nameOrConfig: string | FieldHookConfig<TValue>,
): [
    { name: string; value: TValue; onChange: (...event: any[]) => void; onBlur: () => void },
    { touched: boolean; error?: string },
    {
        setValue: (value: TValue) => void;
        setTouched: (isTouched: boolean) => void;
        setError: (error?: string) => void;
    },
] => {
    const config: FieldHookConfig<TValue> =
        typeof nameOrConfig === 'string' ? { name: nameOrConfig } : nameOrConfig;
    const rhf = useReactHookFormContext();

    const controller = useController({
        name: config.name,
        control: rhf.control,
        rules: {
            validate: config.validate
                ? async (value) => {
                      const result = await config.validate?.(value as any);
                      return result ?? true;
                  }
                : undefined,
        },
    });

    const touched = controller.fieldState.isTouched || rhf.formState.submitCount > 0;
    const error = controller.fieldState.error?.message;

    return [
        controller.field as any,
        { touched, error },
        {
            setValue: (value) => controller.field.onChange(value),
            setTouched: (isTouched) => {
                const current = rhf.getValues(config.name as any);
                rhf.setValue(config.name as any, current as any, {
                    shouldTouch: isTouched,
                    shouldDirty: false,
                    shouldValidate: false,
                });
            },
            setError: (nextError) => {
                if (!nextError) {
                    rhf.clearErrors(config.name as any);
                    return;
                }
                rhf.setError(config.name as any, { type: 'manual', message: nextError });
            },
        },
    ];
};
